import { useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  RotateCw,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { useLocale } from "@/context/LanguageContext";
import {
  ReportPeriodType,
  ReportStatus,
  type OrgReportListItemFragment,
} from "@/graphql/generated";
import { ReportStatusBadge } from "./ReportStatusBadge";
import { useDownloadReport } from "../hooks/useReports";
import type {
  ReportSortKey,
  SortDirection,
  SortState,
} from "../utils/sortReports";

interface Props {
  reports: OrgReportListItemFragment[];
  sort: SortState;
  onSortChange: (key: ReportSortKey) => void;
  onRegenerate: (report: OrgReportListItemFragment) => void;
  onDelete: (report: OrgReportListItemFragment) => void;
}

function relativeTime(iso: string, locale: "de" | "en" | "tr"): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1)
    return locale === "de" ? "gerade eben" : locale === "tr" ? "az önce" : "just now";
  if (minutes < 60)
    return locale === "de"
      ? `vor ${minutes} Min.`
      : locale === "tr"
        ? `${minutes} dk önce`
        : `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24)
    return locale === "de"
      ? `vor ${hours} Std.`
      : locale === "tr"
        ? `${hours} sa önce`
        : `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30)
    return locale === "de"
      ? `vor ${days} T.`
      : locale === "tr"
        ? `${days} gün önce`
        : `${days} d ago`;
  return date.toLocaleDateString(
    locale === "de" ? "de-DE" : locale === "tr" ? "tr-TR" : "en-US",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  );
}

interface SortHeaderProps {
  label: string;
  sortKey: ReportSortKey;
  active: boolean;
  direction: SortDirection;
  align?: "left" | "right";
  onClick: (key: ReportSortKey) => void;
}

function SortHeader({ label, sortKey, active, direction, align = "left", onClick }: SortHeaderProps) {
  const ariaSort = !active ? "none" : direction === "asc" ? "ascending" : "descending";
  const Icon = !active ? ArrowUpDown : direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`group inline-flex items-center gap-1.5 rounded transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          active ? "text-foreground" : ""
        }`}
      >
        <span>{label}</span>
        <Icon
          className={`h-3.5 w-3.5 transition-opacity ${
            active ? "opacity-100" : "opacity-40 group-hover:opacity-80"
          }`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

function PeriodTypeBadge({ type }: { type: ReportPeriodType }) {
  const { t } = useLocale();
  const isMonthly = type === ReportPeriodType.Monthly;
  const isQuarterly = type === ReportPeriodType.Quarterly;
  if (!isMonthly && !isQuarterly) return null;
  const shortLabel = t(isMonthly ? "reports.badge.monthlyShort" : "reports.badge.quarterlyShort");
  const fullLabel = t(isMonthly ? "reports.badge.monthly" : "reports.badge.quarterly");
  return (
    <span
      aria-label={fullLabel}
      title={fullLabel}
      className="ml-2 inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
    >
      {shortLabel}
    </span>
  );
}

export function ReportsTable({ reports, sort, onSortChange, onRegenerate, onDelete }: Props) {
  const { t, locale } = useLocale();
  const downloadReport = useDownloadReport();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    setDownloadError(null);
    try {
      const result = await downloadReport(id);
      if (!result?.downloadUrl) {
        setDownloadError(t("reports.error.downloadUnavailable"));
      }
    } catch {
      setDownloadError(t("reports.error.generic"));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      {downloadError ? (
        <div className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          {downloadError}
        </div>
      ) : null}
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted">
          <tr>
            <SortHeader
              label={t("reports.table.column.period")}
              sortKey="period"
              active={sort.key === "period"}
              direction={sort.direction}
              onClick={onSortChange}
            />
            <SortHeader
              label={t("reports.table.column.generated")}
              sortKey="generated"
              active={sort.key === "generated"}
              direction={sort.direction}
              onClick={onSortChange}
            />
            <SortHeader
              label={t("reports.table.column.status")}
              sortKey="status"
              active={sort.key === "status"}
              direction={sort.direction}
              onClick={onSortChange}
            />
            <th
              scope="col"
              className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              {t("reports.table.column.actions")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {reports.map((report) => (
            <tr key={report._id} className="hover:bg-muted/40">
              <td className="px-4 py-3 text-sm font-medium text-foreground">
                <span className="inline-flex items-center">
                  {report.periodLabel}
                  <PeriodTypeBadge type={report.periodType} />
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                {relativeTime(report.generatedAt ?? report.createdAt, locale)}
              </td>
              <td className="px-4 py-3">
                <ReportStatusBadge status={report.status} />
                {report.status === ReportStatus.Failed && report.errorMessage ? (
                  <p className="mt-1 text-xs text-destructive">
                    {report.errorMessage}
                  </p>
                ) : null}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-2">
                  {report.status === ReportStatus.Complete ? (
                    <button
                      type="button"
                      onClick={() => handleDownload(report._id)}
                      disabled={downloadingId === report._id}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("reports.action.download")}
                    </button>
                  ) : null}
                  {report.status === ReportStatus.Complete ||
                  report.status === ReportStatus.Failed ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onRegenerate(report)}
                        title={t("reports.action.regenerateTooltip")}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition hover:bg-muted"
                      >
                        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t("reports.action.regenerate")}
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(report)}
                        title={t("reports.action.deleteTooltip")}
                        aria-label={t("reports.action.delete")}
                        className="inline-flex items-center justify-center rounded-md border border-border bg-background p-1.5 text-muted-foreground shadow-sm transition hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
